import { useEffect } from "react";
import { Link, useRoute } from "wouter";
import { ArrowLeft, CalendarDays, Loader2, Pencil, Plus, Sparkles, Users } from "lucide-react";
import { useGroup, type GroupEvent } from "../lib/api/useGroups";
import { eventLabel, getEventType } from "@shared/eventTypes";
import Logo from "@/components/Logo";
import { EventCard } from "../components/EventCard";
import type { EventCard as EventCardType } from "../lib/api/useEventsList";

function groupEventToCard(event: GroupEvent): EventCardType {
  return {
    slug: event.slug,
    link: `/events/${event.slug}`,
    name: event.name,
    description: null,
    date: event.date,
    time: event.time,
    endTime: null,
    types: [],
    posterLabel: eventLabel([]),
    coverImageUrl: event.coverImageUrl,
    host: { firstName: "", lastName: null, group: null },
    isOutclose: false,
    isRecurring: false,
    venue: { name: event.venue.name, neighborhood: null, city: event.venue.city },
    capacity: null,
    priceCents: null,
    attendeeCount: event.attendeeCount,
    interestedCount: event.interestedCount ?? 0,
    isPast: event.isPast,
    isRsvped: false,
    isInterested: false,
  };
}

export default function GroupPublic() {
  const [, params] = useRoute("/g/:slug");
  const slug = params?.slug ?? null;
  const { data, isLoading, error } = useGroup(slug);

  const group = data?.success ? data.data : null;

  useEffect(() => {
    if (group) document.title = `${group.name} — Outclose`;
    else document.title = "Group — Outclose";
  }, [group]);

  return (
    <div className="min-h-screen pb-28 md:pb-16">
      <Logo withIcon={true} className="md:hidden justify-center pt-4" />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4">
        <Link
          href="/events"
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400 hover:text-primary-500 transition-colors mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          All events
        </Link>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-7 h-7 animate-spin text-primary-500" />
        </div>
      )}

      {error && (
        <div className="text-center py-24 text-neutral-500 dark:text-neutral-400 px-4">
          Group not found.
        </div>
      )}

      {group && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {/* Header */}
          <div className="flex items-start gap-5 mb-6">
            {group.imageUrl ? (
              <img
                src={group.imageUrl}
                alt={group.name}
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover shrink-0 bg-base-200"
              />
            ) : (
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full shrink-0 bg-gradient-to-br from-primary-500/60 via-secondary-500/50 to-accent-500/60 flex items-center justify-center">
                <Users className="w-8 h-8 text-white/80" />
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold leading-tight">{group.name}</h1>
                </div>
                {group.isOwner && (
                  <Link
                    href={`/g/${group.slug}/edit`}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border bg-base-200 border-base-300 hover:bg-base-300 transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Edit
                  </Link>
                )}
              </div>

              {group.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {group.tags.map((tag) => {
                    const t = getEventType(tag);
                    return (
                      <span
                        key={tag}
                        className="px-2.5 py-0.5 rounded-full bg-base-200 text-neutral-600 dark:text-neutral-400 text-xs font-medium"
                      >
                        {t ? `${t.emoji} ${t.label}` : tag}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {group.description && (
            <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed mb-8">
              {group.description}
            </p>
          )}

          {/* Events */}
          <div>
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-primary-500" />
                Events
              </h2>
              {group.isOwner && group.events.length > 0 && (
                <Link
                  href={`/events/new?group=${group.id}`}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add event
                </Link>
              )}
            </div>

            {group.events.length === 0 &&
              (group.isOwner ? (
                <div className="text-center py-12 px-4 rounded-2xl border border-dashed border-base-300 bg-gradient-to-b from-base-200/40 to-transparent">
                  <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-gradient-to-br from-primary-500/70 via-secondary-500/60 to-accent-500/70 flex items-center justify-center shadow-sm">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-neutral-800 dark:text-neutral-100 font-semibold text-base">
                    Your stage is set ✨
                  </p>
                  <p className="text-neutral-500 dark:text-neutral-400 text-sm mt-1.5 mb-5 max-w-xs mx-auto leading-relaxed">
                    No events yet — create your first one and start bringing your
                    people together.
                  </p>
                  <Link
                    href={`/events/new?group=${group.id}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors shadow-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add your first event
                  </Link>
                </div>
              ) : (
                <p className="text-neutral-500 dark:text-neutral-400 text-sm py-6 text-center">
                  No upcoming events yet.
                </p>
              ))}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {group.events.map((event) => (
                <EventCard key={event.slug} event={groupEventToCard(event)} largeCards={false} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
